import React, { useEffect, useRef, useState } from "react";
import CommonMovieCard from "./CommonMovieCard";
import PairwiseMoviePresentation from "./PairwiseMoviePresentation";
import PageWrapper from "./PageWrapper";
import AlertModal from "./AlertModal";
import { mlriComparisonAction } from "../service/api";
import "./MlriRecommendationTask.css";

export const MLRI_INTRO = {
  title: "Recommendation Lists",
  body: [
    "Based on your earlier movie choices, we have prepared one or more recommendation lists for you.",
    "Please look through all movies in each list before rating it.",
    "For each list, we will ask how well the recommendations reflect your movie preferences and whether they contain movies you would genuinely consider watching.",
    "If more than one distinct list is available, you will also be able to indicate which list best reflects your preferences.",
    "There are no right or wrong answers.",
  ],
};
export const MLRI_ITEMS = [
  { id: "fit", text: "The movies recommended in this list match my preferences." },
  { id: "consideration", text: "I would watch or rewatch the movies recommended in this list, given the opportunity." },
];
export const MLRI_SCALE = ["Strongly disagree", "Disagree", "Neither agree nor disagree", "Agree", "Strongly agree"];

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function MlriRecommendationTask({ userId, sessionId, onComplete }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState(null);
  const [fit, setFit] = useState(null);
  const [consideration, setConsideration] = useState(null);
  const [direct, setDirect] = useState(null);
  const [judgeReasonsVisible, setJudgeReasonsVisible] = useState(false);
  const exposurePending = useRef(new Set());

  useEffect(() => {
    let active = true;
    setLoading(true);
    mlriComparisonAction("", { userId, sessionId }).then((next) => {
      if (active) { setState(next); setLoading(false); }
    }).catch(() => { if (active) { setError("Recommendations could not be loaded. Please try again."); setLoading(false); } });
    return () => { active = false; };
  }, [userId, sessionId]);

  const act = async (action, payload = {}) => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const next = await mlriComparisonAction(action, { userId, sessionId, ...payload });
      setState(next);
      setFit(null); setConsideration(null); setDirect(null); setJudgeReasonsVisible(false);
      if (next.status === "complete") onComplete();
    } catch (failure) { setError(failure.response?.data?.message ?? "Your response could not be saved. Please try again."); }
    finally { setBusy(false); }
  };

  const phase = state?.phase;
  const cluster = phase?.kind === "carousel" ? state.visibleClusters.find((c) => c.clusterId === phase.clusterId) : null;
  const page = cluster?.pages.find((p) => p.page === phase.pageNumber);
  useEffect(() => {
    if (!cluster || !page || page.exposed) return undefined;
    const key = `${cluster.clusterId}:${page.page}`;
    const pending = exposurePending.current;
    if (pending.has(key)) return undefined;
    pending.add(key);
    const frame = requestAnimationFrame(() => {
      mlriComparisonAction("page-exposure", { userId, sessionId, clusterId: cluster.clusterId,
        pageNumber: page.page, clientEventId: uuid() })
        .then(setState)
        .catch(() => setError("Page exposure could not be saved. Please try again."))
        .finally(() => pending.delete(key));
    });
    return () => { cancelAnimationFrame(frame); pending.delete(key); };
  }, [cluster, page, userId, sessionId]);

  if (loading) return <p role="status">Preparing your recommendation lists…</p>;
  if (!state) return <div role="alert">{error}<button type="button" onClick={() => window.location.reload()}>Try again</button></div>;
  const nav = (target) => act("navigate", { target, clientEventId: uuid() });
  const clusterIndex = cluster ? state.visibleClusters.findIndex((c) => c.clusterId === cluster.clusterId) : -1;
  const previous = () => {
    if (phase.kind === "carousel") {
      if (clusterIndex === 0) nav({ kind: "intro" });
      else nav({ kind: "carousel", clusterId: state.visibleClusters[clusterIndex - 1].clusterId, pageNumber: 1 });
    } else if (phase.kind === "direct_comparison" || phase.kind === "probe" || phase.kind === "completion") {
      const probeIndex = state.probeOrder.indexOf(phase.probeId);
      if (phase.kind === "completion" && state.probeOrder.length)
        nav({ kind: "probe", probeId: state.probeOrder.at(-1) });
      else if (phase.kind === "completion" && state.directComparison.applicable)
        nav({ kind: "direct_comparison" });
      else if (phase.kind === "probe" && probeIndex > 0)
        nav({ kind: "probe", probeId: state.probeOrder[probeIndex - 1] });
      else if (phase.kind === "probe" && state.directComparison.applicable)
        nav({ kind: "direct_comparison" });
      else nav({ kind: "carousel", clusterId: state.visibleClusters.at(-1).clusterId, pageNumber: 1 });
    }
  };
  const errorView = error && <p className="mlri-error" role="alert">{error}</p>;

  if (phase.kind === "intro") return <PageWrapper as="section" className="mlri-task common-intro mlri-intro">
    <div className="mlri-intro-content">
      <h2>{MLRI_INTRO.title}</h2>
      {MLRI_INTRO.body.map((text, index) => <p key={text}>{index === 1 ? <strong>{text}</strong> : text}</p>)}
      {errorView}
      <button type="button" className="submit-button" disabled={busy}
        onClick={() => state.introAcknowledged
          ? nav({ kind: "carousel", clusterId: state.visibleClusters[0].clusterId, pageNumber: 1 })
          : act("intro", { clientEventId: uuid() })}>View recommendations</button>
    </div>
  </PageWrapper>;

  if (phase.kind === "carousel" && cluster && page) {
    const saved = state.evaluations.find((r) => r.clusterId === cluster.clusterId)?.response;
    const values = { fit: fit ?? saved?.fit, consideration: consideration ?? saved?.consideration };
    const canSubmitEvaluation = cluster.canEvaluate && Number.isInteger(values.fit) && Number.isInteger(values.consideration);
    const submitEvaluation = (event) => {
      event.preventDefault();
      if (!cluster.canEvaluate) {
        setWarning({
          title: "Please view the full list",
          message: "Please use Next to view all 10 movies in this recommendation list before continuing.",
        });
        return;
      }
      if (!Number.isInteger(values.fit) || !Number.isInteger(values.consideration)) {
        setWarning({
          title: "Please answer both questions",
          message: "Please answer both rating questions before continuing.",
        });
        return;
      }
      act("list-evaluation", {
        clusterId: cluster.clusterId, fit: values.fit, consideration: values.consideration, clientResponseId: uuid(),
      });
    };
    return <section className="mlri-task" aria-labelledby="mlri-list-title">
      <p className="mlri-progress">Recommendation list {clusterIndex + 1} of {state.visibleClusters.length}</p>
      <h2 id="mlri-list-title">{state.visibleClusters.length === 1 ? "Your recommendation list" : cluster.label}</h2>
      <div className="mlri-page-controls">
        <button type="button" className="mlri-secondary-button" aria-label="Previous five recommendations" disabled={busy || page.page === 1 || !page.exposed}
          onClick={() => nav({ kind: "carousel", clusterId: cluster.clusterId, pageNumber: 1 })}>← Previous</button>
        <span aria-live="polite">{page.range}</span>
        <button type="button" className="mlri-secondary-button" aria-label="Next five recommendations" disabled={busy || page.page === 2 || !page.exposed}
          onClick={() => nav({ kind: "carousel", clusterId: cluster.clusterId, pageNumber: 2 })}>Next →</button>
      </div>
      <div className="mlri-card-grid" data-testid="mlri-visible-page">
        {page.cards.map((movie) => <div className="mlri-card-slot" key={movie.movieId}>
          <p className="mlri-card-rank">{movie.rank}</p><CommonMovieCard movie={movie} />
        </div>)}
      </div>
      {!cluster.canEvaluate && <p role="status">View both pages of this list before rating it.</p>}
      <form onSubmit={submitEvaluation}>
        {MLRI_ITEMS.map((item) => <fieldset className="mlri-rating" key={item.id} disabled={!cluster.canEvaluate || busy}>
          <legend>{item.text}</legend>
          <div className="mlri-scale">{MLRI_SCALE.map((label, index) => <label key={label}>
            <input type="radio" name={item.id} value={index + 1} checked={values[item.id] === index + 1}
              onChange={() => item.id === "fit" ? setFit(index + 1) : setConsideration(index + 1)} />
            <span>{index + 1}<small>{label}</small></span>
          </label>)}</div>
        </fieldset>)}
        {errorView}
        <div className="mlri-actions">
          <button type="button" className="mlri-secondary-button" onClick={previous} disabled={busy || !page.exposed}>Back</button>
          <button type="submit" className={canSubmitEvaluation ? "submit-button" : "mlri-secondary-button"}
            disabled={busy}>Save ratings and continue</button>
        </div>
      </form>
      <AlertModal isOpen={!!warning} onClose={() => setWarning(null)} type="warning"
        title={warning?.title} message={warning?.message} />
    </section>;
  }

  if (phase.kind === "direct_comparison") return <section className="mlri-task">
    <h2>{state.directComparison.question}</h2>
    <fieldset className="mlri-choice" disabled={busy || !!state.directComparison.response}>
      <legend>Select one answer</legend>
      {state.directComparison.options.map((option) => <label key={option.clusterId}>
        <input type="radio" name="best-list" value={option.clusterId}
          checked={(state.directComparison.response?.choice ?? direct) === option.clusterId}
          onChange={() => setDirect(option.clusterId)} />{option.label}
      </label>)}
    </fieldset>
    {errorView}
    <div className="mlri-actions"><button type="button" className="mlri-secondary-button" onClick={previous} disabled={busy}>Back</button>
      {state.directComparison.response ? <button type="button" className="submit-button"
        onClick={() => nav(state.nextRequiredView)} disabled={busy}>Continue</button>
        : <button type="button" className="submit-button" disabled={busy || !direct}
          onClick={() => act("direct-comparison", { choice: direct, clientResponseId: uuid() })}>Save choice and continue</button>}
    </div>
  </section>;

  if (phase.kind === "probe" && state.probe) {
    const probe = state.probe;
    const respond = (rawResponse, cannotJudgeReason = null) => act("probe", {
      probeId: probe.probeId, rawResponse, cannotJudgeReason, clientResponseId: uuid(),
    });
    return <section className="mlri-task">
      <h2>{probe.prompt}</h2>
      <div className="mlri-probe-cards"><article><PairwiseMoviePresentation movie={probe.left} /></article>
        <article><PairwiseMoviePresentation movie={probe.right} /></article></div>
      {probe.response ? <p role="status">Your response has been saved.</p> : <div className="mlri-probe-options">
        <button type="button" className="mlri-secondary-button" disabled={busy} onClick={() => respond("choose_left")}>I prefer the left movie</button>
        <button type="button" className="mlri-secondary-button" disabled={busy} onClick={() => respond("choose_right")}>I prefer the right movie</button>
        <button type="button" className="mlri-secondary-button" disabled={busy} onClick={() => respond("reject_both")}>I don't like these two movies</button>
        <button type="button" className="mlri-secondary-button" disabled={busy} onClick={() => respond("approximately_equal")}>I genuinely have no preference between these two movies</button>
        <button type="button" className="mlri-secondary-button" disabled={busy} onClick={() => setJudgeReasonsVisible(true)}>I genuinely can't judge</button>
        {judgeReasonsVisible && <div className="mlri-judge-reasons" role="group" aria-label="Why can't you judge?">
          <button type="button" className="mlri-secondary-button" onClick={() => respond("cannot_judge", "insufficient_information_left")}>I do not know enough about the left movie</button>
          <button type="button" className="mlri-secondary-button" onClick={() => respond("cannot_judge", "insufficient_information_right")}>I do not know enough about the right movie</button>
          <button type="button" className="mlri-secondary-button" onClick={() => respond("cannot_judge", "insufficient_information_both")}>I do not know enough about both movies</button>
        </div>}
      </div>}
      {errorView}
      <div className="mlri-actions"><button type="button" className="mlri-secondary-button" onClick={previous} disabled={busy}>Back</button>
        {probe.response && <button type="button" className="submit-button" onClick={() => nav(state.nextRequiredView)} disabled={busy}>Continue</button>}
      </div>
    </section>;
  }

  return <section className="mlri-task">
    <h2>Recommendation lists complete</h2>
    <p className="mlri-completion-copy">Your responses are ready to save.</p>
    {errorView}
    <div className="mlri-actions"><button type="button" className="mlri-secondary-button" onClick={previous} disabled={busy}>Back</button>
      <button type="button" className="submit-button" disabled={busy || !state.canComplete}
        onClick={() => act("complete", { clientResponseId: uuid() })}>Finish study</button></div>
  </section>;
}
